// src/services/referralRescheduleMatch.js
//
// Firebase-free matching for the referral schedule import: decides, for each
// row extracted from an uploaded schedule PDF, whether the patient is already
// in the pending reminder queue, and if so how to fold the new upload in.
//
// A queued row is the SAME PATIENT when the MRN matches AND the name plausibly
// matches (compareNames tolerates "DOE, JANE A" vs "Jane Doe", a dropped middle
// initial, or a one-letter misread). Then:
//
//   "duplicate"  — same date + time + location already queued (e.g. the same
//                  PDF uploaded twice). Nothing is written.
//   "reschedule" — a different appointment. The LATEST upload wins: the queued
//                  row is updated in place to the new details. No staff step —
//                  a patient with two appointments a few days apart is rare
//                  enough that a moved appointment is the right assumption.
//   "new"        — no queued row is this patient.
//
// Rows with no MRN (e.g. manually added numbers) or no name never match.
//
// Kept pure so it can be unit-tested (referralRescheduleMatch.test.js).

import { compareNames } from "./patientDuplicateMatch";

export const normalizeMrn = (value) =>
  String(value == null ? "" : value).replace(/\s+/g, "").trim().toUpperCase();

// "9:00 AM" / "09:00 am" / "9:00" -> "09:00"; "2:30 PM" / "14:30" -> "14:30".
// Staff edits and PDF extraction format times differently, and a formatting
// difference must not read as a rescheduled appointment. A time with no AM/PM
// follows the same clinic-hours rule as the server (functions/referralSchedule
// assumedMeridiem): 1-6 means PM, so "3:00" matches "3:00 PM". Unparseable
// input is compared as trimmed upper-case text.
export const normalizeApptTime = (value) => {
  const s = String(value || "").trim();
  const m = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
  if (!m) return s.toUpperCase();
  let hh = parseInt(m[1], 10);
  const ap = m[3] ? m[3].toUpperCase() : "";
  if (ap === "PM" && hh !== 12) hh += 12;
  if (ap === "AM" && hh === 12) hh = 0;
  if (!ap && hh >= 1 && hh <= 6) hh += 12;
  return `${String(hh).padStart(2, "0")}:${m[2]}`;
};

const apptSortKey = (row) =>
  `${String(row.appointmentDate || "").trim() || "9999-99-99"} ${normalizeApptTime(row.appointmentTime)}`;

const sameAppointment = (a, b) =>
  String(a.appointmentDate || "").trim() === String(b.appointmentDate || "").trim() &&
  normalizeApptTime(a.appointmentTime) === normalizeApptTime(b.appointmentTime) &&
  String(a.locationCode || "").trim().toUpperCase() ===
    String(b.locationCode || "").trim().toUpperCase();

// MRN must match exactly (ignoring spaces/case) AND the names must plausibly be
// the same person. Either blank -> not the same patient.
export const isSamePatient = (a, b) => {
  const mrn = normalizeMrn(a && a.mrn);
  if (!mrn || mrn !== normalizeMrn(b && b.mrn)) return false;
  return compareNames(a.patientName, b.patientName) !== null;
};

// Classify ONE imported row against the queue. `queuedRows` are pending docs
// ({ id, mrn, patientName, appointmentDate, appointmentTime, locationCode, ... }).
//
// Returns { kind, matches } — for "reschedule", `matches` is every queued row
// for this patient (the caller keeps one and removes the rest).
export function classifyImportedRow(row, queuedRows) {
  const matches = (queuedRows || []).filter((q) => q && row && isSamePatient(row, q));
  if (!matches.length) return { kind: "new", matches: [] };
  const twin = matches.find((q) => sameAppointment(q, row));
  if (twin) return { kind: "duplicate", matches: [twin] };
  return { kind: "reschedule", matches };
}

// Within ONE upload, the same patient twice on different times is two slots on
// the same schedule (e.g. a test then a provider visit), not a reschedule. Keep
// only the EARLIEST so the one text names the time they first need to arrive.
// Rows that can't be matched (no MRN/name) all pass through. Order preserved.
export function collapseSamePatientInBatch(rows) {
  const kept = [];
  for (const r of rows || []) {
    if (!r) continue;
    const i = kept.findIndex((k) => isSamePatient(k, r));
    if (i === -1) kept.push(r);
    else if (apptSortKey(r) < apptSortKey(kept[i])) kept[i] = r;
  }
  return kept;
}

// Of several queued rows for the same patient, the one to update in place:
// prefer a row that already owns a patient link (so they keep ONE link and a
// submitted form still cancels the reminder), else the most recently created.
// `createdMillis(row)` is passed in so this module stays Firebase-free.
export function pickRowToKeep(matches, createdMillis = () => 0) {
  const list = [...(matches || [])];
  const linked = list.find((m) => m.token && m.link);
  if (linked) return linked;
  list.sort((a, b) => createdMillis(b) - createdMillis(a));
  return list[0];
}

// The warning the extractor adds when a PDF row has no usable mobile.
const NO_MOBILE_WARNING = /no valid mobile/i;

// Plain field values that turn `existing` into the newly uploaded appointment.
// The caller adds the Firestore-only parts (see ReferralPage): clearing
// `sends.fortyEightHour` + `needsManualSend`, and re-stamping `scheduledAt`.
//
// Taken from the NEW upload: name, MRN, date, time, location, extraction
// metadata, and the mobile number when the new PDF has one.
// Kept from the EXISTING row: its id, site, `scheduled` flag, patient link
// (token/link/recipientId), any legacy 7-day send state, and a phone number
// when the new PDF has none (staff may have typed one in by hand).
export function buildRescheduleFields(existing, incoming, nowIso) {
  const e = existing || {};
  const r = incoming || {};
  const newPhone = r.mobilePhone || "";
  const keepOldPhone = !newPhone && Boolean(e.phone) && e.sendable !== false;
  const warnings = (Array.isArray(r.warnings) ? r.warnings : []).filter(
    (w) => !(keepOldPhone && NO_MOBILE_WARNING.test(w))
  );

  return {
    patientName: r.patientName || e.patientName || "",
    firstName: r.firstName || e.firstName || "",
    mrn: r.mrn || e.mrn || "",
    appointmentDate: r.appointmentDate || "",
    appointmentTime: r.appointmentTime || "",
    locationCode: r.locationCode || "",
    locationName: r.locationName || "",
    phone: keepOldPhone ? e.phone : newPhone,
    mobilePhone: keepOldPhone ? e.mobilePhone || e.phone : newPhone,
    sendable: keepOldPhone ? true : Boolean(r.sendable),
    sourcePage: r.sourcePage || 0,
    confidence: typeof r.confidence === "number" ? r.confidence : null,
    warnings,
    source: "schedule_pdf",
    // Shown as a small "Rescheduled" badge; informational only.
    rescheduledFrom: {
      appointmentDate: e.appointmentDate || "",
      appointmentTime: e.appointmentTime || "",
      locationCode: e.locationCode || "",
      at: nowIso,
    },
  };
}
